import { Quest, select } from "gamedata/GameData";
import { Fragment } from "react";
import { redirect, useLoaderData, useNavigate, useParams } from "react-router";
import { z } from "zod";
import { InputMD } from "~/components/form/InputMd";
import { ItemLink } from "~/components/item/ItemLink";
import { NpcLink } from "~/components/npc/NpcLink";
import { QuestLink } from "~/components/quest/QuestLink";
import { QuestLocationList } from "~/components/quest/QuestLocationList";
import { QuestProfList } from "~/components/quest/QuestProfList";
import { QuestRaceList } from "~/components/quest/QuestRaceList";
import { QuestStartNPCList } from "~/components/quest/QuestStartNPCList";
import { Comments, getComments } from "~/components/site/Comments";
import { PencilIcon } from "~/components/site/icons/Pencil";
import { DiscordLink } from "~/components/site/links/Socials";
import { MdContent } from "~/components/site/MdContent";
import { NotFoundInfo } from "~/components/site/NotFoundInfo";
import { UserLink } from "~/components/site/UserLink";
import { Breadcrumb } from "~/components/ui/Breadcrumb";
import { Button } from "~/components/ui/Button";
import { Facts } from "~/components/ui/Facts";
import { Heading3 } from "~/components/ui/Heading";
import { InfoBox } from "~/components/ui/InfoBox";
import { PageTitle } from "~/components/ui/PageTitle";
import { Tabs } from "~/components/ui/Tabs";
import { stripSlashes } from "~/util/common";
import { decodeInt } from "~/util/encdec";
import { GameVer, tt, tturl, useRealm, useT, useTurl } from "~/util/Lang";
import type { GetLoaderData, MetaFunc, OurLoaderArgs } from "~/util/Loaders";
import { callApi, createLoader } from "~/util/Loaders";
import { getDescription, questMdPage } from "~/util/quest";
import { logPageView } from "~/util/stats";
import { homePage } from "~/util/titles";
import { editPageApi } from "../api/mdPages.pages";
import css from "./Quest.module.css";

function checkDepth<T>(maxDepth: number, obj: T): T {
  if (maxDepth <= 0) {
    throw new Error("Object depth max check failed");
  }

  const typ = typeof obj;
  if (Array.isArray(obj)) {
    obj.forEach((el) => checkDepth(maxDepth - 1, el));
  } else if (typ === "object" && obj) {
    for (const v of Object.values(obj)) {
      checkDepth(maxDepth - 1, v);
    }
  } else if (
    obj === null ||
    typ === "undefined" ||
    typ === "string" ||
    typ === "number" ||
    typ === "boolean"
  ) {
  } else {
    throw new Error(`Function checkDepth does not support type: ${typ}`);
  }
  return obj;
}

type QuestAlt = {
  id: number;
  name: string;
  uname: string;
  gver: GameVer;
};

async function loadData(
  la: OurLoaderArgs,
): Promise<
  | { status: "404"; alts: QuestAlt[] }
  | { status: "ok"; data: Awaited<ReturnType<typeof makeData>> }
> {
  const t = tt.bind(null, la.realm.lang);
  const params = Params.parse(la.params);
  const [idStr] = params.questId.split("-", 2);
  const id = parseInt(idStr);
  const quest = la.gd.quests.get(id);

  if (quest == null) {
    la.setStatusCode(404);
    const alts: QuestAlt[] = [];
    for (const altGD of Object.values(la.gd.all_gd!)) {
      const altQuest = altGD.quests.get(id);
      if (altQuest) {
        alts.push({
          id: altQuest.id,
          name: t(altQuest.name),
          uname: altQuest.uname,
          gver: altGD.gver,
        });
      }
    }
    return { status: "404", alts };
  }

  return { status: "ok", data: await makeData(la, quest) };
}

export const loader = createLoader(async (la) => {
  return loadData(la);
});

const Params = z.object({ questId: z.string() });
async function makeData(la: OurLoaderArgs, quest: Quest) {
  const t = tt.bind(null, la.realm.lang);

  if (quest == null) {
    throw Response.json(
      { msg: t({ en: "Quest not found.", ru: "Квест не найден." }) },
      { status: 404 },
    );
  }

  // Log
  logPageView(la);
  //

  const realm = la.realm;
  const idealBasePath = tturl(realm, `/quests/${quest.uname}`);

  if (!la.url.pathname.startsWith(idealBasePath)) {
    const redirectUrl = new URL(idealBasePath, la.url.origin);
    const remainingPath = la.url.pathname.slice(idealBasePath.length);
    redirectUrl.pathname += remainingPath;

    const searchParams = new URLSearchParams(la.url.searchParams);
    searchParams.delete("_data");
    redirectUrl.search = searchParams.toString();
    redirectUrl.hash = la.url.hash;

    throw redirect(redirectUrl.toString(), 301);
  }

  const authUser = la.req.sess?.user;

  const data = {
    authUser,
    quest: select(quest, {
      id: true,
      uname: true,
      name: { en: true, ru: true },
      desc: t,
      another: true,
      walkthrough: (x) => select(x[realm.lang], (x) => checkDepth(6, x)),
      progs: (progs) =>
        select(progs[realm.lang], {
          select: {
            progId: true,
            name: true,
            desc: true,
            items: {
              select: {
                item: {
                  itemName: true,
                  name: t,
                  icon: true,
                  crystalType: true,
                },
                count: true,
              },
            },
          },
        }),
      minLevel: true,
      maxLevel: true,
      requirements: {
        category: true,
        items: { select: { select: { name: t } } },
        occupation: {
          select: { id: true, profName: true, name: t },
        },
        races: {
          select: { raceName: true, icon: true, name: t },
        },
      },
      startNpcs: {
        select: {
          id: true,
          npcName: true,
          name: t,
          level: true,
          nick: t,
          nickColor: true,
          locs: {
            select: { locName: true, name: t },
          },
        },
      },
      npcs: {
        select: {
          id: true,
          npcName: true,
          name: t,
          level: true,
          nick: t,
          nickColor: true,
        },
      },
      type: true,
    }),
  };

  return {
    ...data,
    mdPage: await questMdPage(la, data.quest.id),
    comments: await getComments({
      id: data.quest.id + "",
      type: "quest",
      args: { gd: la.gd, realm: la.realm },
    }),
  };
}

type LoaderData = GetLoaderData<typeof loader>;
export type LoaderPageQuest = LoaderData;

export const meta: MetaFunc<LoaderData> = (cx) => {
  if (cx.data.status === "404") {
    return {
      title: "Quest not found - " + homePage(cx.realm),
    };
  }

  const quest = cx.data.data.quest;
  const name = cx.t({
    en: quest.name.en,
    ru: `${quest.name.ru} (${quest.name.en})`,
  });

  return {
    title: `${name} - ${cx.t({ en: "Quests", ru: "Квесты" })} - ${homePage(
      cx.realm,
    )}`,
    description: getDescription({ realm: cx.realm, quest }),
    ogTitle: `Quest: ${cx.t(quest.name)}`,
    canonicalUrl: `/quests/${quest.uname}`,
  };
};

export default function QuestPage() {
  const params = useParams();
  const loaderData = useLoaderData<typeof loader>();
  const t = useT();
  const turl = useTurl();
  const realm = useRealm();
  const navigate = useNavigate();
  const questsTitle = t({ en: "Quests", ru: "Квесты" });

  if (loaderData.status === "404") {
    return (
      <NotFoundInfo
        title={t({ en: "Quest not found", ru: "Квест не найден" })}
        altsTitle={t({
          en: "This quest exists in other gave versions:",
          ru: "Этот квест доступен в других версиях игры:",
        })}
        alts={loaderData.alts.map((altQuest) => ({
          gver: altQuest.gver,
          node: <QuestLink quest={altQuest} />,
        }))}
      />
    );
  }

  const data = loaderData.data;

  const breadcrumbList = [
    { title: questsTitle, url: turl("/quests") },
    {
      title: t(data.quest.name),
      url: turl(`/quests/${data.quest.uname}`),
    },
  ];

  async function editQuestPage() {
    const id = data.quest.id;
    const page = `/quests/${params.questId}`;
    if (!data.authUser) {
      const ref = turl(page);
      return navigate(`/login?r=${ref}`);
    }
    // setLoading(true);
    if (data.mdPage) {
      return navigate(`${page}/edit-page`);
    }

    const result = await callApi(editPageApi, {
      input: {
        game: [],
        target: { _: "quest", questId: id },
      },
      realm,
    });

    switch (result.t) {
      case "success":
        // setLoading(false);
        return navigate(turl(`${page}/edit-page`));
      case "error":
        console.log(result);

        // setLoading(false);
        break;
    }
  }
  console.log(data.mdPage);

  return (
    <div>
      <div className="content">
        <Breadcrumb items={breadcrumbList} />

        <div className="columns">
          <div className="column is-three-quarters">
            <PageTitle>{t(data.quest.name)}</PageTitle>

            <div style={{ marginBottom: "5px" }}>{data.quest.desc}</div>

            <>
              <Heading3>{t({ en: "Walkthrough", ru: "Прохождение" })}</Heading3>
              <div className="walkthrough">
                <ol>
                  {data.quest.walkthrough.map((step, i: number) => {
                    const text = step.pieces.map((piece, i: number) => {
                      if (typeof piece === "string") {
                        return <Fragment key={i}>{piece}</Fragment>;
                      }
                      switch (piece.t) {
                        case "item":
                          return (
                            <ItemLink
                              key={i}
                              item={{
                                itemName: piece.id,
                                name: piece.name,
                                icon: piece.icon,
                              }}
                              count={decodeInt({
                                value: piece.count ?? 0,
                                datatype: "quest",
                                uname: piece.id,
                              })}
                            />
                          );
                        case "npc":
                          return (
                            <NpcLink
                              key={i}
                              npc={{
                                npcName: piece.id,
                                name: piece.name,
                                nick: piece.title,
                                type: piece.type,
                                nickColor: piece.nickColor,
                              }}
                              showLvl={false}
                            />
                          );
                        case "quest":
                          return <QuestLink key={i} quest={piece} />;
                        case "exp":
                          return (
                            <span key={i} style={{ color: "#e1e1e1" }}>
                              {decodeInt({
                                value: piece.exp ?? 0,
                                datatype: "quest",
                                uname: "exp",
                              })}{" "}
                              {t({ en: "EXP", ru: "ЭКСП" })}
                            </span>
                          );
                        case "sp":
                          return (
                            <span key={i} style={{ color: "#e1e1e1" }}>
                              {decodeInt({
                                value: piece.sp ?? 0,
                                datatype: "quest",
                                uname: "sp",
                              })}{" "}
                              {t({ en: "SP", ru: "СП" })}
                            </span>
                          );
                        case "by":
                          return (
                            <noindex key={i} style={{ opacity: 0 }}>
                              {" "}
                              By l2hub
                            </noindex>
                          );
                      }
                    });
                    return (
                      <li key={i}>
                        <div>{text}</div>
                      </li>
                    );
                  })}
                </ol>
              </div>
            </>
            {data.quest.walkthrough.length === 0 && (
              <>
                {data.mdPage && data.mdPage.content && (
                  <MdContent>{data.mdPage.content}</MdContent>
                )}
                <div
                  className={css.contributeBox}
                  style={{
                    background: "url(/s/bg/bg1.jpg) 0 0 / cover no-repeat",
                  }}
                >
                  <div className={css.wrapper}>
                    <div style={{ marginBottom: 10 }}>
                      This quest doesn't have a description, but you can help us
                      add it.
                    </div>
                    <div>
                      <Button
                        type="button"
                        color="green"
                        onClick={() => editQuestPage()}
                      >
                        <PencilIcon width={20} height={20} />
                        {data.mdPage ? "Edit description" : "Add description"}
                      </Button>
                    </div>
                  </div>
                </div>
              </>
            )}

            {data.quest.progs.length > 0 && (
              <>
                <Heading3>
                  {t({
                    en: "Steps from the game client",
                    ru: "Шаги из клиента игры",
                  })}
                </Heading3>
                <div className="walkthrough">
                  <ol>
                    {data.quest.progs.map((prog, i) => {
                      return (
                        <li key={i}>
                          <div>
                            <div
                              style={{
                                color: "#ccc",
                                fontWeight: "600",
                                paddingBottom: 8,
                              }}
                            >
                              {prog.name}
                            </div>
                            <div style={{ whiteSpace: "pre-wrap" }}>
                              {prog.desc}
                            </div>
                          </div>
                          {prog.items.length > 0 && (
                            <div style={{ paddingTop: 4 }}>
                              {t({ en: "Items: ", ru: "Предметы: " })}
                              {prog.items.map(({ item, count }, i) => (
                                <Fragment key={i}>
                                  {i > 0 && ", "}
                                  <ItemLink item={item} count={count} />
                                </Fragment>
                              ))}
                            </div>
                          )}
                        </li>
                      );
                    })}
                  </ol>
                </div>
              </>
            )}
          </div>
          <div className="column">
            <InfoBox title={t({ en: "Quest Facts", ru: "Краткая информация" })}>
              <Facts
                facts={[
                  { name: "Quest ID", value: data.quest.id },
                  {
                    name: "Англ. название",
                    value: data.quest.name.en,
                    show: realm.lang === "ru",
                  },
                  {
                    name: t({ en: "Level", ru: "Уровень" }),
                    value: (
                      <>
                        {data.quest.minLevel ?? ""}
                        {data.quest.maxLevel ? "-" + data.quest.maxLevel : null}
                      </>
                    ),
                    show: !!data.quest.minLevel || !!data.quest.maxLevel,
                  },
                  {
                    name: t({ en: "Repeat", ru: "Повторяемый" }),
                    value:
                      data.quest.type === "repeatable"
                        ? t({ en: "Yes", ru: "Да" })
                        : t({ en: "No", ru: "Нет" }),
                  },
                  {
                    name: t({ en: "Race", ru: "Расы" }),
                    value: (
                      <QuestRaceList races={data.quest.requirements.races} />
                    ),
                    show: data.quest.requirements.races.length > 0,
                  },
                  {
                    name: t({ en: "Class", ru: "Класс" }),
                    value: (
                      <QuestProfList
                        profs={data.quest.requirements.occupation}
                      />
                    ),
                    show: data.quest.requirements.occupation.length > 0,
                  },
                  {
                    name: t({ en: "Location", ru: "Локация" }),
                    value: (
                      <QuestLocationList startNpcs={data.quest.startNpcs} />
                    ),
                  },
                  {
                    name: t({ en: "Start NPC", ru: "Стартовый НПЦ" }),
                    value: (
                      <QuestStartNPCList
                        startNpcs={data.quest.startNpcs}
                        separator={true}
                      />
                    ),
                  },
                ]}
              />
              <InputMD value={`:quest[${data.quest.id}]`} />
            </InfoBox>
            {data.mdPage && (
              <InfoBox title={t({ en: "Contributors", ru: "Участники" })}>
                <ul>
                  {data.mdPage.contributors.map((user, i) => (
                    <li key={i}>
                      <UserLink user={user} />
                    </li>
                  ))}
                </ul>
              </InfoBox>
            )}
            <InfoBox title={t({ en: "Information", ru: "Информация" })}>
              <div style={{ padding: "0 7px" }}>
                <div>Found a mistake in content?</div>
                <div style={{ paddingBottom: "10px" }}>Help us fix it!</div>
                <DiscordLink type="button" />
              </div>
            </InfoBox>
          </div>
        </div>
      </div>
      <Tabs
        current={params["*"] ?? ""}
        link={(id) => turl(stripSlashes(`/quests/${data.quest.uname}/${id}`))}
        tabs={[
          {
            id: "comments",
            title: t({ en: "Comments", ru: "Комментарии" }),
            body: () => (
              <div>
                <Comments
                  comments={data.comments}
                  authUser={data.authUser}
                  subject={{ objType: "quest", objUname: data.quest.id + "" }}
                />
              </div>
            ),
          },
        ]}
      />
    </div>
  );
}
